/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useContext } from 'react';
import PropTypes from "prop-types";
import { Droppable } from 'react-beautiful-dnd';
import classNames from 'classnames';
import { OuiDragDropContextContext } from './drag_drop_context';
var spacingToClassNameMap = {
  none: null,
  s: 'ouiDroppable--s',
  m: 'ouiDroppable--m',
  l: 'ouiDroppable--l'
};
export var OuiDroppableContext = /*#__PURE__*/React.createContext({
  cloneItems: false
});
export var OuiDroppable = ({
  droppableId,
  direction,
  isDropDisabled = false,
  children,
  className,
  cloneDraggables = false,
  spacing = 'none',
  style,
  type = 'OUI_DEFAULT',
  withPanel = false,
  grow = false,
  'data-test-subj': dataTestSubj = 'droppable',
  ...rest
}) => {
  const {
    isDraggingType
  } = useContext(OuiDragDropContextContext);
  const dropIsDisabled = cloneDraggables ? true : isDropDisabled;
  return <Droppable isDropDisabled={dropIsDisabled} droppableId={droppableId} direction={direction} type={type} {...rest}>
      {(provided, snapshot) => {
      const classes = classNames('ouiDroppable', {
        'ouiDroppable--isDisabled': dropIsDisabled,
        'ouiDroppable--isDraggingOver': snapshot.isDraggingOver,
        'ouiDroppable--isDraggingType': isDraggingType === type,
        'ouiDroppable--withPanel': withPanel,
        'ouiDroppable--grow': grow,
        'ouiDroppable--noGrow': !grow
      }, spacingToClassNameMap[spacing], className);
      const placeholderClasses = classNames('ouiDroppable__placeholder', {
        'ouiDroppable__placeholder--isHidden': cloneDraggables
      });
      const DroppableElement = typeof children === 'function' ? children(provided, snapshot) : children;
      return <div {...provided.droppableProps} ref={provided.innerRef} style={style} data-test-subj={dataTestSubj} className={classes}>
            <OuiDroppableContext.Provider value={{
          cloneItems: cloneDraggables
        }}>
              {DroppableElement}
            </OuiDroppableContext.Provider>
            <div className={placeholderClasses}>{provided.placeholder}</div>
          </div>;
    }}
    </Droppable>;
};
OuiDroppable.propTypes = {
  /**
     * ReactNode to render as this component's content
     */
  children: PropTypes.oneOfType([PropTypes.element.isRequired, PropTypes.arrayOf(PropTypes.element.isRequired).isRequired, PropTypes.any.isRequired]).isRequired,
  className: PropTypes.string,
  /**
     * Makes its items immutable. Dragging creates cloned items that can be dropped elsewhere.
     */
  cloneDraggables: PropTypes.bool,
  style: PropTypes.any,
  /**
     * Adds padding to the droppable area
     */
  spacing: PropTypes.oneOf(["none", "s", "m", "l"]),
  /**
     * Adds an OuiPanel style to the droppable area
     */
  withPanel: PropTypes.bool,
  /**
     * Allow the panel to flex-grow?
     */
  grow: PropTypes.bool,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};
try {
  OuiDroppable.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src2210237963/src/src/components/drag_and_drop/droppable.tsx',
    description: '',
    displayName: 'OuiDroppable',
    methods: [],
    props: {
      children: {
        defaultValue: null,
        description: "ReactNode to render as this component's content",
        name: 'children',
        parent: {
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        },
        declarations: [{
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'ReactElement<any, string | JSXElementConstructor<any>> | ReactElement<any, string | JSXElementConstructor<any>>[] | ((provided: DroppableProvided, snapshot: DroppableStateSnapshot) => ReactElement<...>)'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        },
        declarations: [{
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      cloneDraggables: {
        defaultValue: {
          value: 'false'
        },
        description: 'Makes its items immutable. Dragging creates cloned items that can be dropped elsewhere.',
        name: 'cloneDraggables',
        parent: {
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        },
        declarations: [{
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      style: {
        defaultValue: null,
        description: '',
        name: 'style',
        parent: {
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        },
        declarations: [{
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        }],
        required: false,
        type: {
          name: 'CSSProperties'
        }
      },
      spacing: {
        defaultValue: {
          value: 'none'
        },
        description: 'Adds padding to the droppable area',
        name: 'spacing',
        parent: {
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        },
        declarations: [{
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "l" | "none"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"none"'
          }]
        }
      },
      withPanel: {
        defaultValue: {
          value: 'false'
        },
        description: 'Adds an OuiPanel style to the droppable area',
        name: 'withPanel',
        parent: {
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        },
        declarations: [{
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      grow: {
        defaultValue: {
          value: 'false'
        },
        description: 'Allow the panel to flex-grow?',
        name: 'grow',
        parent: {
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        },
        declarations: [{
          fileName: 'src/src/components/drag_and_drop/droppable.tsx',
          name: 'OuiDroppableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      direction: {
        defaultValue: null,
        description: '',
        name: 'direction',
        parent: {
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'Direction',
          value: [{
            value: '"horizontal"'
          }, {
            value: '"vertical"'
          }]
        }
      },
      type: {
        defaultValue: {
          value: 'OUI_DEFAULT'
        },
        description: '',
        name: 'type',
        parent: {
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      mode: {
        defaultValue: null,
        description: '',
        name: 'mode',
        parent: {
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'DroppableMode',
          value: [{
            value: '"standard"'
          }, {
            value: '"virtual"'
          }]
        }
      },
      droppableId: {
        defaultValue: null,
        description: '',
        name: 'droppableId',
        parent: {
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      isDropDisabled: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'isDropDisabled',
        parent: {
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isCombineEnabled: {
        defaultValue: null,
        description: '',
        name: 'isCombineEnabled',
        parent: {
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      ignoreContainerClipping: {
        defaultValue: null,
        description: '',
        name: 'ignoreContainerClipping',
        parent: {
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      renderClone: {
        defaultValue: null,
        description: '',
        name: 'renderClone',
        parent: {
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        }],
        required: false,
        type: {
          name: 'DraggableChildrenFn'
        }
      },
      getContainerForClone: {
        defaultValue: null,
        description: '',
        name: 'getContainerForClone',
        parent: {
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DroppableProps'
        }],
        required: false,
        type: {
          name: '() => HTMLElement'
        }
      }
    },
    extendedInterfaces: ['OuiDroppableProps', 'CommonProps']
  };
} catch (e) {}